import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";

import axios from "axios";

type Hackathon = {
  _id: string;
  title: string;
  status: "Upcoming" | "Active" | "Completed";
  category: string;
  description: string;
  startDate: string;
  endDate: string;
};

const computeStatus = (startDate: string, endDate: string): "Upcoming" | "Active" | "Completed" => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) {
    return "Upcoming";
  } else if (now >= start && now <= end) {
    return "Active";
  } else {
    return "Completed";
  }
};

export default function HackathonListPage() {
  const navigate = useNavigate();
  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchHackathons = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/hackathon-events/`
        );

        // Handle different response formats
        let data = res.data;
        console.log("API Response:", data);

        if (data && typeof data === 'object' && !Array.isArray(data)) {
          // If response is wrapped in an object, try to extract the array
          data = data.items || data.data || data.hackathons || [];
        }

        // Ensure data is an array
        if (Array.isArray(data)) {
          // Enrich hackathons with computed status
          const enriched = data.map(h => ({
            ...h,
            status: computeStatus(h.startDate, h.endDate)
          }));
          setHackathons(enriched);
        } else {
          setHackathons([]);
          setError("Invalid data format from server");
          console.error("Data is not an array:", data);
        }
      } catch (err: any) {
        setError("Failed to fetch hackathons");
        setHackathons([]);
        console.error("Fetch error:", err);
      }
      setLoading(false);
    };
    fetchHackathons();
  }, []);

  const [activeTab, setActiveTab] = useState<"All" | "Active" | "Upcoming" | "Completed">("All");

  const STATUS_ORDER: Record<string, number> = { Active: 0, Upcoming: 1, Completed: 2 };

  const filteredHackathons = (() => {
    const list = activeTab === "All"
      ? [...hackathons]
      : hackathons.filter((h) => h.status === activeTab);
    if (activeTab === "All") {
      list.sort((a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3));
    }
    return list;
  })();

  const countByStatus = (status: "Upcoming" | "Active" | "Completed") => {
    if (!Array.isArray(hackathons)) return 0;
    return hackathons.filter((h) => h.status === status).length;
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Page Header */}
        <div className="px-6 lg:px-10 pt-10">
          <h1 className="text-4xl font-bold text-[#0058AB]">Hackathons</h1>
          <p className="text-gray-500 mt-1">Browse active and upcoming hackathons.</p>
        </div>

        {/* Search Bar */}
        <div className="px-6 lg:px-10 mt-6">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            <input
              type="search"
              placeholder="Search hackathons..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500"
             
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-nowrap gap-4 px-4 sm:px-6 lg:px-10 mt-4 text-sm font-semibold overflow-x-auto">
          {[
            { title: "All", count: hackathons.length },
            { title: "Active", count: countByStatus("Active") },
            { title: "Upcoming", count: countByStatus("Upcoming") },
            { title: "Completed", count: countByStatus("Completed") },
          ].map((tab) => (
            <div
              key={tab.title}
              className="cursor-pointer"
              onClick={() => setActiveTab(tab.title as any)}
            >
              <div
                className={`flex items-center gap-2 pb-1 ${activeTab === tab.title ? "text-blue-600" : "text-gray-500"
                  }`}
              >
                {tab.title}
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-xs text-blue-600 flex-shrink-0">
                  {tab.count}
                </span>
              </div>
              {activeTab === tab.title && (
                <div className="h-[2px] bg-blue-600 rounded-full" />
              )}
            </div>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-6 lg:px-10 mt-10">
          {/* Upcoming Card */}
          <div className="flex flex-row justify-between items-start w-full min-h-[100px] px-5 py-4 bg-white border border-[rgba(228,232,238,0.5)] rounded-2xl shadow-sm">
            {/* Left Column */}
            <div className="flex flex-col gap-2">
              <p className="text-[10px] font-normal uppercase tracking-[0.5px] text-[#8B93A8]">
                Upcoming Events
              </p>
              <p className="text-[38px] font-normal text-[#0058AB] leading-[46px] tracking-[-0.95px]">
                {countByStatus("Upcoming")}
              </p>
            </div>
            {/* Right Icon Box */}
            <div className="flex justify-center items-center w-[69px] h-[69px] bg-[#F0F6FF] rounded-2xl">
              <span className="text-[40px]"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3334 3.3335V10.0002" stroke="#0058AB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M26.6666 3.3335V10.0002" stroke="#0058AB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M31.6666 6.6665H8.33334C6.49238 6.6665 5 8.1589 5 9.99985V33.3333C5 35.1742 6.49238 36.6664 8.33334 36.6664H31.6666C33.5075 36.6664 35 35.1742 35 33.3333V9.99985C35 8.1589 33.5075 6.6665 31.6666 6.6665Z" stroke="#0058AB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5 16.6665H35" stroke="#0058AB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              </span>
            </div>
          </div>

          {/* Active Card */}
          <div className="flex flex-row justify-between items-start w-full min-h-[100px] px-5 py-4 bg-white border border-[rgba(228,232,238,0.5)] rounded-2xl shadow-sm">
            {/* Left Column */}
            <div className="flex flex-col gap-2">
              <p className="text-[10px] font-normal uppercase tracking-[0.5px] text-[#8B93A8]">
                Active Events
              </p>
              <p className="text-[38px] font-normal text-[#0058AB] leading-[46px] tracking-[-0.95px]">
                {countByStatus("Active")}
              </p>
            </div>
            {/* Right Icon Box */}
            <div className="flex justify-center items-center w-[69px] h-[69px] bg-[#F0F6FF] rounded-2xl">
              <span className="text-[40px]"><svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 16C0 7.16346 7.16344 0 16 0H31.9894C40.826 0 47.9894 7.16344 47.9894 16V31.9894C47.9894 40.826 40.826 47.9894 31.9894 47.9894H16C7.16346 47.9894 0 40.826 0 31.9894V16Z" fill="#FFFBF0" />
                <path d="M26.1086 25.4109C26.2502 24.7029 26.6041 24.2074 27.1704 23.6411C27.8783 23.004 28.2323 22.0837 28.2323 21.1634C28.2323 20.0369 27.7847 18.9566 26.9882 18.16C26.1917 17.3635 25.1113 16.916 23.9848 16.916C22.8584 16.916 21.778 17.3635 20.9815 18.16C20.1849 18.9566 19.7374 20.0369 19.7374 21.1634C19.7374 21.8713 19.879 22.7208 20.7993 23.6411C21.2948 24.1366 21.7196 24.7029 21.8611 25.4109" stroke="#FEB100" stroke-width="1.41581" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M21.8611 28.2422H26.1085" stroke="#FEB100" stroke-width="1.41581" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M22.569 31.0737H25.4006" stroke="#FEB100" stroke-width="1.41581" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              </span>
            </div>
          </div>

          {/* Completed Card */}
          <div className="flex flex-row justify-between items-start w-full min-h-[100px] px-5 py-4 bg-white border border-[rgba(228,232,238,0.5)] rounded-2xl shadow-sm">
            {/* Left Column */}
            <div className="flex flex-col gap-2">
              <p className="text-[10px] font-normal uppercase tracking-[0.5px] text-[#8B93A8]">
                Completed Events
              </p>
              <p className="text-[38px] font-normal text-[#0058AB] leading-[46px] tracking-[-0.95px]">
                {countByStatus("Completed")}
              </p>
            </div>
            {/* Right Icon Box */}
            <div className="flex justify-center items-center w-[69px] h-[69px] bg-[#F0F6FF] rounded-2xl">
              <span className="text-[40px]"><svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 16C0 7.16346 7.16344 0 16 0H31.9894C40.826 0 47.9894 7.16344 47.9894 16V31.9894C47.9894 40.826 40.826 47.9894 31.9894 47.9894H16C7.16346 47.9894 0 40.826 0 31.9894V16Z" fill="#FFFBF0" />
                <path
                  d="M17 24L22 29L31 20"
                  stroke="#0070FF"
                  strokeWidth="2.0"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg></span>
            </div>
          </div>
        </div>

        {/* Hackathon Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 lg:px-10 mt-10">
          {loading ? (
            <div className="col-span-2 text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="col-span-2 text-center text-red-500">{error}</div>
          ) : filteredHackathons.length === 0 ? (
            <div className="col-span-2 text-center text-gray-500">No hackathons found.</div>
          ) : (
            filteredHackathons.map((hack) => (
              <div
                key={hack._id}
                onClick={() => navigate(`/user-dashboard/hackathon-detail/${hack._id}`)}
                className={`bg-white border rounded-2xl shadow-sm p-6 border-t-4
                ${hack.status === "Upcoming"
                    ? "border-yellow-400"
                    : hack.status === "Active"
                      ? "border-blue-600"
                      : "border-green-600"
                  }
                cursor-pointer hover:shadow-md transition`}
              >
                {/* Title Row */}
                <div className="items-center">
                  <h3 className="text-xl font-semibold mb-2">{hack.title}</h3>
                  {hack.status === "Upcoming" && (
                    <span className="text-xs px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                      Upcoming
                    </span>
                  )}
                  {hack.status === "Active" && (
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                      Active
                    </span>
                  )}
                  {hack.status === "Completed" && (
                    <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700">
                      Completed
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm mt-5 line-clamp-2">{hack.description}</p>
                <div className="flex justify-between border-t pt-3 mt-4">
                  <span className="text-gray-500 text-xs">
                    {hack.startDate && hack.endDate
                      ? `${new Date(hack.startDate).toLocaleDateString()} – ${new Date(hack.endDate).toLocaleDateString()}`
                      : ""}
                  </span>
                  <span className="text-blue-600 font-medium">View Details →</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
